import {
  doc,
  type DocumentData,
  getDoc,
  getFirestore,
  onSnapshot,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import {
  addMember,
  getActiveMember,
  removeMember,
  updateMemberRole,
} from './members';
import type {
  AddedByUser,
  Chemical,
  Company,
  CreateCompanyData,
  CreateCompanyResponse,
  MeasuringUnit,
} from './models';

const db = getFirestore();

const toDate = (value: unknown): Date | undefined => {
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  return undefined;
};

const getCompanyData = (id: string, data: DocumentData): Company =>
  ({
    ...data,
    id,
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
    ...(data.subscription
      ? {
          subscription: {
            ...data.subscription,
            expirationDate: toDate(data.subscription.expirationDate),
            lastUpdated: toDate(data.subscription.lastUpdated),
          },
        }
      : {}),
  }) as Company;

// Create a new company (admin only)
export const createCompany = async (
  data: CreateCompanyData
): Promise<Company> => {
  try {
    const createCompanyFunction = httpsCallable<
      CreateCompanyData,
      CreateCompanyResponse
    >(functions, 'createCompany');

    const result = await createCompanyFunction(data);

    const today = new Date();
    const company: Company = {
      ...result.data.company,
      createdAt: result.data.company.createdAt
        ? new Date(result.data.company.createdAt)
        : today,
      updatedAt: result.data.company.updatedAt
        ? new Date(result.data.company.updatedAt)
        : today,
    };

    return company;
  } catch (error) {
    console.error('Error creating company:', error);

    const errorMessages = {
      'functions/invalid-argument':
        error instanceof Error ? error.message : 'Invalid argument',
      'functions/already-exists':
        'User already belongs to a company. Users can only create one company.',
      'functions/not-found': 'User account not found.',
      'functions/unauthenticated': 'Please log in to create a company.',
      'functions/internal': 'Server error. Please try again later.',
    };

    const errorCode =
      error instanceof Error && 'code' in error ? (error.code as string) : '';
    const errorMessage =
      errorMessages[errorCode as keyof typeof errorMessages] ||
      'Failed to create company';

    throw new Error(errorMessage);
  }
};

// Get a company by ID
export const getCompany = async (
  companyId: string
): Promise<Company | null> => {
  try {
    const docRef = doc(db, 'companies', companyId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return getCompanyData(docSnap.id, data);
    }

    return null;
  } catch (error) {
    console.error('Error getting company:', error);
    throw new Error('Failed to get company');
  }
};

// Listen to company changes
export const subscribeToCompany = (
  companyId: string,
  callback: (company: Company | null) => void
): (() => void) => {
  const docRef = doc(db, 'companies', companyId);

  return onSnapshot(docRef, (doc) => {
    if (doc.exists()) {
      const data = doc.data();
      const company = getCompanyData(doc.id, data);
      callback(company);
    } else {
      callback(null);
    }
  });
};

// Update company details (admin only)
export const updateCompanyDetails = async (
  companyId: string,
  updates: {
    name?: string;
    email?: string;
    phone?: string;
    address?: string;
  },
  userId: string
): Promise<void> => {
  try {
    // Check user permissions
    const permissions = await checkUserPermissions(companyId, userId);
    if (!permissions.canManageSettings) {
      throw new Error('Only admins can update company details');
    }

    const updateData: any = {
      ...updates,
      updatedAt: Timestamp.now(),
      updatedBy: userId,
    };

    await updateDoc(doc(db, 'companies', companyId), updateData);
  } catch (error) {
    console.error('Error updating company details:', error);
    throw error;
  }
};

// Add team member (admin only)
export const addTeamMember = async (
  companyId: string,
  memberData: {
    userId: string;
    email: string;
    name: string;
    role: 'admin' | 'technician';
  },
  addedBy: AddedByUser
): Promise<void> => {
  try {
    // Check user permissions
    const permissions = await checkUserPermissions(companyId, addedBy.id);
    if (!permissions.canManageTeam) {
      throw new Error('Only admins can add team members');
    }

    await addMember(companyId, memberData, addedBy);

    await updateDoc(doc(db, 'companies', companyId), {
      updatedAt: Timestamp.now(),
      updatedBy: addedBy.id,
    });
  } catch (error) {
    console.error('Error adding team member:', error);
    throw error;
  }
};

// Remove team member (admin only)
export const removeTeamMember = async (
  companyId: string,
  userId: string,
  removedBy: string
): Promise<void> => {
  try {
    // Check user permissions
    const permissions = await checkUserPermissions(companyId, removedBy);
    if (!permissions.canManageTeam) {
      throw new Error('Only admins can remove team members');
    }

    await removeMember(companyId, userId);

    await updateDoc(doc(db, 'companies', companyId), {
      updatedAt: Timestamp.now(),
      updatedBy: removedBy,
    });
  } catch (error) {
    console.error('Error removing team member:', error);
    throw error;
  }
};

// Update member role (admin only)
export const updateTeamMemberRole = async (
  companyId: string,
  userId: string,
  newRole: 'admin' | 'technician',
  updatedBy: string
): Promise<void> => {
  try {
    // Check user permissions
    const permissions = await checkUserPermissions(companyId, updatedBy);
    if (!permissions.canManageTeam) {
      throw new Error('Only admins can update member roles');
    }

    await updateMemberRole(companyId, userId, newRole);

    await updateDoc(doc(db, 'companies', companyId), {
      updatedAt: Timestamp.now(),
      updatedBy,
    });
  } catch (error) {
    console.error('Error updating member role:', error);
    throw error;
  }
};

// Check user permissions for a company
export const checkUserPermissions = async (
  companyId: string,
  userId: string
): Promise<{
  isMember: boolean;
  isAdmin: boolean;
  isOwner: boolean;
  canManageSettings: boolean;
  canManageTeam: boolean;
}> => {
  try {
    const company = await getCompany(companyId);
    if (!company) {
      return {
        isMember: false,
        isAdmin: false,
        isOwner: false,
        canManageSettings: false,
        canManageTeam: false,
      };
    }

    const member = await getActiveMember(companyId, userId);
    const isMember = !!member;
    const isAdmin = member?.role === 'admin';
    const isOwner = company.ownerId === userId;

    return {
      isMember,
      isAdmin,
      isOwner,
      canManageSettings: isAdmin,
      canManageTeam: isAdmin,
    };
  } catch (error) {
    console.error('Error checking user permissions:', error);
    return {
      isMember: false,
      isAdmin: false,
      isOwner: false,
      canManageSettings: false,
      canManageTeam: false,
    };
  }
};

// Update app settings (measurements, date format, etc.)
export const updateAppSettings = async (
  companyId: string,
  settings: {
    measurements?: MeasuringUnit;
    currency?: string;
  },
  userId: string
): Promise<void> => {
  try {
    // Check user permissions
    const permissions = await checkUserPermissions(companyId, userId);
    if (!permissions.canManageSettings) {
      throw new Error('Only admins can update app settings');
    }

    await updateDoc(doc(db, 'companies', companyId), {
      ...settings,
      updatedAt: Timestamp.now(),
      updatedBy: userId,
    });
  } catch (error) {
    console.error('Error updating app settings:', error);
    throw error;
  }
};

// Update company chemicals (admin only)
export const updateCompanyChemicals = async (
  companyId: string,
  chemicals: Chemical[],
  userId: string
): Promise<void> => {
  try {
    // Check user permissions
    const permissions = await checkUserPermissions(companyId, userId);
    if (!permissions.canManageSettings) {
      throw new Error('Only admins can update company chemicals');
    }

    await updateDoc(doc(db, 'companies', companyId), {
      chemicals,
      updatedAt: Timestamp.now(),
      updatedBy: userId,
    });
  } catch (error) {
    console.error('Error updating company chemicals:', error);
    throw error;
  }
};
